import React, { Component } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import { withRouter } from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import axios from "axios";

const BRAND = "#99192D";
const BRAND_HOVER = "#b01e35";
const BRAND_LIGHT = "rgba(153,25,45,0.07)";

const styles = (theme) => ({
  root: {
    minHeight: "100vh",
    backgroundColor: "#f5f5f5",
  },
  header: {
    backgroundColor: BRAND,
    padding: "20px 30px",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    color: "white",
    fontWeight: "bold",
    fontSize: "22px",
  },
  backBtn: {
    color: "white",
    border: "1px solid white",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.15)",
    },
  },
  content: {
    maxWidth: "900px",
    margin: "0 auto",
    padding: "30px 20px",
  },
  subtitle: {
    color: "#555",
    marginBottom: "20px",
    fontSize: "15px",
  },
  /* ── Drop Zone ── */
  dropZone: {
    border: `2px dashed ${BRAND}`,
    borderRadius: "10px",
    backgroundColor: "white",
    padding: "36px 20px",
    textAlign: "center",
    cursor: "pointer",
    marginBottom: "28px",
    transition: "background 0.2s",
  },
  dropZoneActive: {
    border: `2px dashed ${BRAND}`,
    borderRadius: "10px",
    backgroundColor: BRAND_LIGHT,
    padding: "36px 20px",
    textAlign: "center",
    cursor: "pointer",
    marginBottom: "28px",
    transition: "background 0.2s",
  },
  dropIcon: {
    fontSize: "48px",
    color: BRAND,
    marginBottom: "10px",
  },
  dropText: {
    color: BRAND,
    fontWeight: "bold",
    fontSize: "16px",
    marginBottom: "4px",
  },
  dropSubText: {
    color: "#888",
    fontSize: "13px",
  },
  docCard: {
    backgroundColor: "white",
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "20px",
    marginBottom: "16px",
    position: "relative",
  },
  docCardHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: "14px",
  },
  docNumber: {
    backgroundColor: BRAND,
    color: "white",
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: "bold",
    marginRight: "10px",
    flexShrink: 0,
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  filePickerBtn: {
    border: `2px dashed ${BRAND}`,
    borderRadius: "6px",
    padding: "10px 18px",
    color: BRAND,
    textTransform: "none",
    fontSize: "14px",
    minWidth: "180px",
    "&:hover": {
      backgroundColor: BRAND_LIGHT,
    },
  },
  fileName: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: "#333",
    fontSize: "14px",
    fontStyle: "italic",
  },
  descField: {
    marginTop: "14px",
    width: "100%",
  },
  deleteBtn: {
    position: "absolute",
    top: "10px",
    right: "10px",
    color: "#999",
    "&:hover": {
      color: BRAND,
    },
  },
  addBtn: {
    border: `2px solid ${BRAND}`,
    color: BRAND,
    borderRadius: "25px",
    padding: "10px 24px",
    textTransform: "none",
    fontSize: "15px",
    marginTop: "8px",
    "&:hover": {
      backgroundColor: BRAND_LIGHT,
    },
  },
  uploadBtn: {
    backgroundColor: BRAND,
    color: "white",
    borderRadius: "25px",
    padding: "12px 40px",
    fontSize: "16px",
    textTransform: "none",
    marginTop: "24px",
    "&:hover": {
      backgroundColor: BRAND_HOVER,
    },
    "&:disabled": {
      backgroundColor: "#ccc",
      color: "#999",
    },
  },
  successBox: {
    backgroundColor: "#e8f5e9",
    border: "1px solid #a5d6a7",
    borderRadius: "8px",
    padding: "20px",
    textAlign: "center",
    marginTop: "20px",
  },
  errorBox: {
    backgroundColor: "#ffebee",
    border: "1px solid #ef9a9a",
    borderRadius: "8px",
    padding: "14px",
    marginTop: "12px",
    color: "#b71c1c",
    fontSize: "14px",
  },
  requiredNote: {
    color: "#999",
    fontSize: "13px",
    marginBottom: "20px",
  },
});

const emptyDoc = (file = null) => ({ id: Date.now() + Math.random(), file, description: "", error: "" });

const ACCEPTED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/tiff",
  "image/bmp",
];
const ACCEPTED_EXT = /\.(pdf|docx|jpe?g|png|gif|webp|tiff?|bmp)$/i;

const isAllowedFile = (file) =>
  ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXT.test(file.name);

class DocumentUploadPage extends Component {
  state = {
    docs: [emptyDoc(), emptyDoc()],
    uploading: false,
    uploadSuccess: false,
    uploadError: "",
    dragOver: false,
  };

  /* ── Drag-and-drop handlers ── */
  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ dragOver: false });
    const droppedFiles = Array.from(e.dataTransfer.files).filter(isAllowedFile);
    if (!droppedFiles.length) return;
    this.addFilesToDocs(droppedFiles);
  };

  handleDropZoneClick = () => {
    this.dropInput.click();
  };

  handleDropZoneInputChange = (e) => {
    const files = Array.from(e.target.files).filter(isAllowedFile);
    if (!files.length) return;
    this.addFilesToDocs(files);
    e.target.value = "";
  };

  addFilesToDocs = (files) => {
    this.setState((prev) => {
      let docs = [...prev.docs];
      files.forEach((file) => {
        const emptyIdx = docs.findIndex((d) => !d.file);
        if (emptyIdx !== -1) {
          docs[emptyIdx] = { ...docs[emptyIdx], file, error: "" };
        } else {
          docs.push(emptyDoc(file));
        }
      });
      return { docs };
    });
  };

  handleAddDoc = () => {
    this.setState((prev) => ({ docs: [...prev.docs, emptyDoc()] }));
  };

  handleRemoveDoc = (id) => {
    this.setState((prev) => ({
      docs: prev.docs.length > 1 ? prev.docs.filter((d) => d.id !== id) : prev.docs,
    }));
  };

  handleFileChange = (id, event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!isAllowedFile(file)) {
      this.setState((prev) => ({
        docs: prev.docs.map((d) =>
          d.id === id ? { ...d, error: this.context.t("docUpload_errorInvalidType") } : d
        ),
      }));
      return;
    }
    this.setState((prev) => ({
      docs: prev.docs.map((d) => (d.id === id ? { ...d, file, error: "" } : d)),
    }));
  };

  handleDescChange = (id, value) => {
    this.setState((prev) => ({
      docs: prev.docs.map((d) =>
        d.id === id ? { ...d, description: value, error: value.trim() ? "" : d.error } : d
      ),
    }));
  };

  readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  validate = () => {
    let valid = true;
    const docs = this.state.docs.map((d) => {
      let error = "";
      if (!d.file) {
        error = this.context.t("docUpload_errorNoFile");
        valid = false;
      } else if (!isAllowedFile(d.file)) {
        error = this.context.t("docUpload_errorInvalidType");
        valid = false;
      } else if (!d.description.trim()) {
        error = this.context.t("docUpload_errorNoDesc");
        valid = false;
      }
      return { ...d, error };
    });
    this.setState({ docs });
    return valid;
  };

  handleUpload = async () => {
    if (!this.validate()) return;
    this.setState({ uploading: true, uploadError: "", uploadSuccess: false });
    try {
      const uploadFileDataArray = await Promise.all(
        this.state.docs.map(async (d) => {
          const base64Result = await this.readFileAsBase64(d.file);
          return {
            uniqueId: localStorage.getItem("token") || "",
            doc_name: d.description.trim(),
            file_name: d.file.name,
            base_data: base64Result.split("base64,")[1],
          };
        })
      );
      const url = `${process.env.REACT_APP_SERVER_HOSTNAME}:${process.env.REACT_APP_SERVER_PORT}/uploadDocumentsOnly`;
      await axios.post(url, { files: uploadFileDataArray });
      this.setState({ uploading: false, uploadSuccess: true });
    } catch (err) {
      this.setState({
        uploading: false,
        uploadError: this.context.t("docUpload_errorUploadFailed"),
      });
    }
  };

  render() {
    const { classes } = this.props;
    const { docs, uploading, uploadSuccess, uploadError, dragOver } = this.state;

    if (uploadSuccess) {
      return (
        <div className={classes.root}>
          <div className={classes.header}>
            <Typography className={classes.headerTitle}>
              {this.context.t("docUpload_title")}
            </Typography>
          </div>
          <div className={classes.content}>
            <div className={classes.successBox}>
              <Typography style={{ fontSize: "20px", fontWeight: "bold", color: "#2e7d32" }}>
                {this.context.t("docUpload_successTitle")}
              </Typography>
              <Typography style={{ marginTop: "8px", color: "#555" }}>
                {this.context.t("docUpload_successMsg")}
              </Typography>
              <Button
                style={{ marginTop: "20px", borderRadius: "20px", textTransform: "none", color: BRAND, border: `1px solid ${BRAND}` }}
                onClick={() => this.props.history.push("/")}
              >
                {this.context.t("docUpload_backHome")}
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>
            {this.context.t("docUpload_title")}
          </Typography>
          <Button className={classes.backBtn} onClick={() => this.props.history.push("/")}>
            {this.context.t("docUpload_back")}
          </Button>
        </div>

        <div className={classes.content}>
          <Typography className={classes.subtitle}>
            {this.context.t("docUpload_subtitle")}
          </Typography>

          {/* ── Drag-and-drop zone ── */}
          <div
            className={dragOver ? classes.dropZoneActive : classes.dropZone}
            onDragOver={this.handleDragOver}
            onDragEnter={this.handleDragOver}
            onDragLeave={this.handleDragLeave}
            onDrop={this.handleDrop}
            onClick={this.handleDropZoneClick}
          >
            <CloudUploadIcon className={classes.dropIcon} />
            <Typography className={classes.dropText}>
              {this.context.t("docUpload_dropText")}
            </Typography>
            <Typography className={classes.dropSubText}>
              {this.context.t("docUpload_dropSubText")} &mdash; PDF, DOCX, Images
            </Typography>
          </div>

          {/* Hidden input for click-to-browse in drop zone */}
          <input
            type="file"
            multiple
            accept=".pdf,.docx,.jpg,.jpeg,.png,.gif,.webp,.tiff,.tif,.bmp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
            style={{ display: "none" }}
            ref={(el) => { this.dropInput = el; }}
            onChange={this.handleDropZoneInputChange}
          />

          <Typography className={classes.requiredNote}>
            * {this.context.t("docUpload_requiredNote")}
          </Typography>

          {docs.map((doc, index) => (
            <div key={doc.id} className={classes.docCard}>
              {docs.length > 1 && (
                <IconButton
                  className={classes.deleteBtn}
                  size="small"
                  onClick={() => this.handleRemoveDoc(doc.id)}
                  title={this.context.t("docUpload_remove")}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}

              <div className={classes.docCardHeader}>
                <div className={classes.docNumber}>{index + 1}</div>
                <Typography style={{ fontWeight: "bold", color: "#333", fontSize: "15px" }}>
                  {this.context.t("docUpload_document")} {index + 1}
                </Typography>
              </div>

              {/* File picker */}
              <div className={classes.fileRow}>
                <input
                  type="file"
                  accept=".pdf,.docx,.jpg,.jpeg,.png,.gif,.webp,.tiff,.tif,.bmp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                  id={`file-input-${doc.id}`}
                  style={{ display: "none" }}
                  onChange={(e) => this.handleFileChange(doc.id, e)}
                />
                <label htmlFor={`file-input-${doc.id}`}>
                  <Button component="span" className={classes.filePickerBtn}>
                    <CloudUploadIcon style={{ marginRight: "8px", fontSize: "18px" }} />
                    {doc.file ? this.context.t("docUpload_changeFile") : this.context.t("docUpload_chooseFile")}
                  </Button>
                </label>
                {doc.file && (
                  <span className={classes.fileName}>
                    <InsertDriveFileIcon style={{ fontSize: "16px", color: BRAND }} />
                    {doc.file.name}
                  </span>
                )}
              </div>

              {/* Description */}
              <TextField
                className={classes.descField}
                label={`${this.context.t("docUpload_descLabel")} *`}
                placeholder={this.context.t("docUpload_descPlaceholder")}
                variant="outlined"
                multiline
                rows={2}
                value={doc.description}
                onChange={(e) => this.handleDescChange(doc.id, e.target.value)}
                error={Boolean(doc.error)}
                helperText={doc.error || ""}
                inputProps={{ maxLength: 500 }}
              />
            </div>
          ))}

          {/* Add document button */}
          <Button className={classes.addBtn} onClick={this.handleAddDoc}>
            <AddIcon style={{ marginRight: "6px", fontSize: "18px" }} />
            {this.context.t("docUpload_addDoc")}
          </Button>

          {/* Error message */}
          {uploadError && <div className={classes.errorBox}>{uploadError}</div>}

          {/* Upload button */}
          <Grid container justifyContent="center">
            <Button
              className={classes.uploadBtn}
              onClick={this.handleUpload}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <CircularProgress size={20} style={{ color: "white", marginRight: "10px" }} />
                  {this.context.t("docUpload_uploading")}
                </>
              ) : (
                <>
                  <CloudUploadIcon style={{ marginRight: "8px" }} />
                  {this.context.t("docUpload_uploadBtn")}
                </>
              )}
            </Button>
          </Grid>

          <div style={{ marginBottom: "60px" }} />
        </div>
      </div>
    );
  }
}

DocumentUploadPage.contextTypes = {
  t: PropTypes.func,
};

export default withRouter(withStyles(styles)(DocumentUploadPage));
